
import { useState } from 'react';

export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    timeline: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d41a9mpk7qvo96j89c8g', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          timeline: '',
          budget: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-white mb-4">
            Book Your Free Consultation
          </h3>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to make your real estate dreams a reality? Let's discuss your goals and create a personalized strategy for your success.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-12">
          <div>
            <div className="bg-white rounded-lg p-8 shadow-lg border-2 border-yellow-500">
              <h4 className="text-2xl font-bold text-black mb-6">Get In Touch</h4>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-black"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-black mb-1">Phone</h5>
                    <p className="text-gray-700">(248) 248-4801</p>
                    <p className="text-sm text-gray-500">Available 6 days a week</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-black"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-black mb-1">Email</h5>
                    <p className="text-gray-700">shantonya@shantonyascott.com</p>
                    <p className="text-sm text-gray-500">Response within 2 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-black"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-black mb-1">Service Areas</h5>
                    <p className="text-gray-700">Wayne, Oakland, Macomb, Saginaw & Kent Counties</p>
                    <p className="text-sm text-gray-500">Michigan</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-black"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-black mb-1">Office Hours</h5>
                    <p className="text-gray-700">Mon-Fri: 9AM-6PM</p>
                    <p className="text-gray-700">Saturday: 9AM-3PM</p>
                    <p className="text-gray-700">Sunday: Closed</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h5 className="font-bold text-black mb-4">Follow Me</h5>
                <div className="flex space-x-4">
                  <a href="https://www.facebook.com/shantonya/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-facebook-fill"></i>
                  </a>
                  <a href="https://www.instagram.com/shantonyatherealtor/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-instagram-line"></i>
                  </a>
                  <a href="https://www.linkedin.com/in/shantonyascott" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill"></i>
                  </a>
                  <a href="https://www.youtube.com/channel/UCptAnnq6T4cA7wtViKJn8qQ" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors cursor-pointer">
                    <i className="ri-youtube-fill"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg p-8 shadow-lg border-2 border-yellow-500" data-readdy-form>
              <h4 className="text-2xl font-bold text-black mb-6">Schedule Your Consultation</h4>
              
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-6">
                  Thank you! Your consultation request has been submitted. I'll contact you within 24 hours.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
                  There was an error submitting your request. Please try again or call directly.
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-5   00 focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                    placeholder="(248) 248-4801"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Needed *
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select a service</option>
                    <option value="buying">Buying a Home</option>
                    <option value="selling">Selling a Home</option>
                    <option value="first-time">First-Time Buyer</option>
                    <option value="investment">Investment Property</option>
                    <option value="waterfront">Waterfront Property</option>
                    <option value="luxury">Luxury Home</option>
                    <option value="relocation">Relocation</option>
                    <option value="consultation">Market Consultation</option>
                  </select>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timeline
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select timeline</option>
                    <option value="immediately">Immediately</option>
                    <option value="1-3-months">1-3 months</option>
                    <option value="3-6-months">3-6 months</option>
                    <option value="6-12-months">6-12 months</option>
                    <option value="just-looking">Just looking</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget Range
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Select budget</option>
                    <option value="under-200k">Under $200k</option>
                    <option value="200k-400k">$200k - $400k</option>
                    <option value="400k-600k">$400k - $600k</option>
                    <option value="600k-800k">$600k - $800k</option>
                    <option value="800k-1m">$800k - $1M</option>
                    <option value="over-1m">Over $1M</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tell me about your goals
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm resize-none"
                  placeholder="Share your real estate goals, preferred areas, must-haves, or any questions you have..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white py-4 px-6 rounded-lg hover:bg-yellow-500 hover:text-black transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line mr-2"></i>
                    Schedule My Consultation
                  </>
                )}
              </button>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                By submitting this form, you agree to be contacted about your real estate needs. 
                Your information is secure and will never be shared.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
